import React, { useState, useEffect } from 'react';
import { Server, Cloud, Code, CheckCircle2, AlertCircle, Wifi, WifiOff, Loader2 } from 'lucide-react';

const A2AServerToggle = () => {
  // Detect if we're on Cloudflare/production
  const isProduction = typeof window !== 'undefined' && 
                       window.location.hostname !== 'localhost' && 
                       window.location.hostname !== '127.0.0.1' &&
                       !window.location.hostname.includes('localhost');
  
  const [useLocalhost, setUseLocalhost] = useState(() => {
    // If on production, force production mode (ignore localStorage)
    if (isProduction) {
      // Clear any localhost preference from localStorage
      localStorage.setItem('a2a_use_localhost', 'false');
      return false;
    }
    
    // For localhost, load from localStorage, default to false (production)
    const saved = localStorage.getItem('a2a_use_localhost');
    return saved === 'true';
  });

  const [connectionStatus, setConnectionStatus] = useState('checking'); // 'checking', 'connected', 'disconnected'
  const [isAnimating, setIsAnimating] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  // Set initial URL immediately on mount (before any A2A calls)
  useEffect(() => {
    // Determine the correct server URL
    const serverUrl = isProduction 
      ? 'https://a2amathsai-server.appsokay.net'
      : (useLocalhost ? 'http://localhost:8787' : 'https://a2amathsai-server.appsokay.net');
    
    // Update the A2A client URL immediately
    if (window.updateA2AServerUrl) {
      window.updateA2AServerUrl(serverUrl);
      console.log(`[A2A Server Toggle] Initial URL set to: ${serverUrl} (isProduction: ${isProduction}, useLocalhost: ${useLocalhost})`);
    }

    // Check connection status
    checkConnectionStatus(serverUrl);
  }, []); // Run only once on mount
  
  // Update URL when toggle state changes
  useEffect(() => {
    // If on production, always use production URL (don't allow localhost)
    if (isProduction) {
      const serverUrl = 'https://a2amathsai-server.appsokay.net';
      if (window.updateA2AServerUrl) {
        window.updateA2AServerUrl(serverUrl);
        console.log(`[A2A Server Toggle] Production mode: Using Cloudflare server`);
      }
      // Force state to false
      if (useLocalhost) {
        setUseLocalhost(false);
      }
      checkConnectionStatus(serverUrl);
      return;
    }
    
    // For localhost, save preference and update URL
    localStorage.setItem('a2a_use_localhost', useLocalhost.toString());
    
    // Update server URL
    const serverUrl = useLocalhost 
      ? 'http://localhost:8787'
      : 'https://a2amathsai-server.appsokay.net';
    
    // Update the A2A client URL immediately
    if (window.updateA2AServerUrl) {
      window.updateA2AServerUrl(serverUrl);
      console.log(`[A2A Server Toggle] Switched to: ${useLocalhost ? 'localhost:8787' : 'Cloudflare production server'}`);
    }

    // Check connection status
    checkConnectionStatus(serverUrl);
  }, [useLocalhost, isProduction]);

  // Check connection status
  const checkConnectionStatus = async (url) => {
    setIsChecking(true);
    setConnectionStatus('checking');
    try {
      const response = await fetch(`${url}/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(3000), // 3 second timeout
      });
      
      if (response.ok) {
        setConnectionStatus('connected');
      } else {
        setConnectionStatus('disconnected');
      }
    } catch (error) {
      setConnectionStatus('disconnected');
    } finally {
      setIsChecking(false);
    }
  };

  const handleToggle = () => {
    if (isProduction) return; // Disabled on production
    
    setIsAnimating(true);
    setUseLocalhost(!useLocalhost);
    
    // Reset animation after animation completes
    setTimeout(() => setIsAnimating(false), 300);
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'text-green-600 dark:text-green-400';
      case 'disconnected':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-yellow-600 dark:text-yellow-400';
    }
  };

  const getStatusBgColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'disconnected':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      default:
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
    }
  };

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'disconnected':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Loader2 className="w-4 h-4 animate-spin" />;
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'disconnected':
        return 'Disconnected';
      default:
        return 'Checking...';
    }
  };

  const currentServerUrl = isProduction 
    ? 'https://a2amathsai-server.appsokay.net'
    : (useLocalhost ? 'http://localhost:8787' : 'https://a2amathsai-server.appsokay.net');

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <div className={`
        rounded-2xl shadow-2xl border-2 p-4 
        backdrop-blur-md bg-white/95 dark:bg-gray-900/95
        ${getStatusBgColor()}
        transition-all duration-300
        ${isAnimating ? 'scale-105' : 'scale-100'}
        min-w-[320px]
      `}>
        {/* Header with Status */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${connectionStatus === 'connected' ? 'bg-green-100 dark:bg-green-900/30' : connectionStatus === 'disconnected' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-yellow-100 dark:bg-yellow-900/30'}`}>
              {connectionStatus === 'connected' ? (
                <Wifi className="w-4 h-4 text-green-600 dark:text-green-400" />
              ) : connectionStatus === 'disconnected' ? (
                <WifiOff className="w-4 h-4 text-red-600 dark:text-red-400" />
              ) : (
                <Loader2 className="w-4 h-4 text-yellow-600 dark:text-yellow-400 animate-spin" />
              )}
            </div>
            <div>
              <div className={`text-sm font-semibold ${getStatusColor()}`}>
                {getStatusText()}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                A2A Server
              </div>
            </div>
          </div>
          
          {/* Server Type Badge */}
          <div className={`
            px-3 py-1 rounded-full text-xs font-semibold
            ${useLocalhost 
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' 
              : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
            }
          `}>
            {useLocalhost ? 'Local' : 'Cloud'}
          </div>
        </div>

        {/* Toggle Switch */}
        <div className="flex items-center justify-between gap-4 py-2">
          <div className="flex items-center gap-2">
            <Code className={`w-5 h-5 transition-colors ${useLocalhost ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
            <span className={`text-sm font-medium ${useLocalhost ? 'text-blue-700 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400'}`}>
              Localhost
            </span>
          </div>

          <label className={`
            relative inline-flex items-center cursor-pointer
            ${isProduction ? 'opacity-50 cursor-not-allowed' : ''}
          `}>
            <input
              type="checkbox"
              checked={useLocalhost}
              onChange={handleToggle}
              disabled={isProduction}
              className="sr-only peer"
            />
            <div className={`
              w-14 h-7 
              ${useLocalhost ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}
              peer-focus:outline-none 
              peer-focus:ring-4 
              peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800
              rounded-full 
              peer 
              transition-all duration-300
              ${isAnimating ? 'scale-110' : ''}
            `}>
              <div className={`
                absolute top-1 left-1
                bg-white dark:bg-gray-200
                rounded-full
                h-5 w-5
                transition-transform duration-300
                ${useLocalhost ? 'translate-x-7' : 'translate-x-0'}
                shadow-lg
              `}></div>
            </div>
          </label>

          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${!useLocalhost ? 'text-green-700 dark:text-green-300' : 'text-gray-500 dark:text-gray-400'}`}>
              Cloudflare
            </span>
            <Cloud className={`w-5 h-5 transition-colors ${!useLocalhost ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`} />
          </div>
        </div>

        {/* Server URL Info */}
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
              Server URL:
            </span>
            <span className="text-xs text-gray-700 dark:text-gray-300 font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
              {useLocalhost ? 'localhost:8787' : 'appsokay.net'}
            </span>
          </div>
          {isProduction && (
            <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              <span>Production mode: Cloudflare only</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default A2AServerToggle;

